import { CloudDownloadOutlined, DownOutlined } from '@ant-design/icons'
import { Button, DatePicker, Dropdown, Menu, Table } from 'antd'
import moment from 'moment'
import React from 'react'

import { useGetStatisticsQuery } from '@/generated/graphql'

import s from './statistics.module.less'

const Legacy = {
  general: { withPostCount: true, withPostPeopleCount: true, withLandingPeopleCount: true, withLandingCount: true },
  centralTendency: { withContributedImpact: true, withImpact: true },
  yesterdayImpact: { withYesterdayImpact: true },
}
type LegacyType = keyof typeof Legacy

export const StatisticsLegacy = () => {
  const [yearMonth, setYearMonth] = React.useState(moment())
  const [type, setType] = React.useState<LegacyType>('general')
  const { data } = useGetStatisticsQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-and-network',
    variables: {
      input: {
        year: yearMonth.year(),
        month: yearMonth.month() + 1,
      },
      ...Legacy[type],
    },
  })

  const dataSource = (() => {
    const days = yearMonth.daysInMonth()
    switch (type) {
      case 'general': {
        const postsCount = data?.statistics.postsCount || []
        const peopleCount = data?.statistics.peopleCount || []
        const landingCount = data?.statistics.landingCount || []
        const landingPeopleCount = data?.statistics.landingPeopleCount || []
        return Array.from({ length: days }, (_x, index) => {
          const date = yearMonth
            .clone()
            .date(index + 1)
            .format('YYYY-MM-DD')
          return {
            key: date,
            date,
            postsCount: postsCount?.find((data) => data.date === date)?.value || null,
            peopleCount: peopleCount?.find((data) => data.date === date)?.value || null,
            landingCount: landingCount?.find((data) => data.date === date)?.value || null,
            landingPeopleCount: landingPeopleCount?.find((data) => data.date === date)?.value || null,
          }
        })
      }
      case 'centralTendency': {
        const impact = data?.statistics.impact || []
        const contributedImpact = data?.statistics.contributedImpact || []
        return Array.from({ length: days }, (_x, index) => {
          const date = yearMonth
            .clone()
            .date(index + 1)
            .format('YYYY-MM-DD')
          return {
            key: date,
            date,
            contributedImpactMean: contributedImpact?.find((data) => data.date === date)?.mean || null,
            contributedImpactMedian: contributedImpact?.find((data) => data.date === date)?.median || null,
            contributedImpactMode: contributedImpact?.find((data) => data.date === date)?.mode || null,
            impactMean: impact?.find((data) => data.date === date)?.mean || null,
            impactMedian: impact?.find((data) => data.date === date)?.median || null,
            impactMode: impact?.find((data) => data.date === date)?.mode || null,
          }
        })
      }
      case 'yesterdayImpact': {
        const yesterdayImpact = data?.statistics.yesterdayImpact || []
        return yesterdayImpact.map((impact) => ({
          key: impact.email,
          name: impact.name,
          email: impact.email,
          impact: impact.impact,
        }))
      }
    }
  })()

  const columns = (() => {
    switch (type) {
      case 'general':
        return [
          {
            title: '日期',
            dataIndex: 'date',
            key: 'date',
          },
          {
            title: '发帖数',
            dataIndex: 'postsCount',
            key: 'postsCount',
          },
          {
            title: '发帖人数',
            dataIndex: 'peopleCount',
            key: 'peopleCount',
          },
          {
            title: '登录人数',
            dataIndex: 'landingPeopleCount',
            key: 'landingPeopleCount',
          },
          {
            title: '登录次数',
            dataIndex: 'landingCount',
            key: 'landingCount',
          },
        ]
      case 'centralTendency':
        return [
          {
            title: '日期',
            dataIndex: 'date',
            key: 'date',
          },
          {
            title: '贡献平均值',
            dataIndex: 'contributedImpactMean',
            key: 'contributedImpactMean',
          },
          {
            title: '贡献中位数',
            dataIndex: 'contributedImpactMedian',
            key: 'contributedImpactMedian',
          },
          {
            title: '贡献众数',
            dataIndex: 'contributedImpactMode',
            key: 'contributedImpactMode',
          },
          {
            title: '影响力平均值',
            dataIndex: 'impactMean',
            key: 'impactMean',
          },
          {
            title: '影响力中位数',
            dataIndex: 'impactMedian',
            key: 'impactMedian',
          },
          {
            title: '影响力众数',
            dataIndex: 'impactMode',
            key: 'impactMode',
          },
        ]
      case 'yesterdayImpact':
        return [
          {
            title: '姓名',
            dataIndex: 'name',
            key: 'name',
          },
          {
            title: '邮箱',
            dataIndex: 'email',
            key: 'email',
          },
          {
            title: '昨日影响力',
            dataIndex: 'impact',
            key: 'impact',
            sorter: (a: { impact: number }, b: { impact: number }) => a.impact - b.impact,
          },
        ]
    }
  })()

  const menu = (
    <Menu selectedKeys={[type]} onClick={({ key }) => setType(key as typeof type)}>
      {Object.keys(Legacy).map((key) => (
        <Menu.Item key={key}>{key}</Menu.Item>
      ))}
    </Menu>
  )

  const actions = (
    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.5em', alignItems: 'baseline' }}>
      <Button
        icon={<CloudDownloadOutlined />}
        type='link'
        onClick={() => {
          const csv = (() => {
            switch (type) {
              case 'general':
                return [['日期', '发帖数', '发帖人数', '登录人数', '登录次数']].concat(
                  dataSource.map((data: any) => [
                    data.date,
                    data.postsCount,
                    data.peopleCount,
                    data.landingPeopleCount,
                    data.landingCount,
                  ])
                )
              case 'centralTendency':
                return [
                  ['日期', '贡献平均值', '贡献中位数', '贡献众数', '影响力平均值', '影响力中位数', '影响力众数'],
                ].concat(
                  dataSource.map((data: any) => [
                    data.date,
                    data.contributedImpactMean,
                    data.contributedImpactMedian,
                    data.contributedImpactMode,
                    data.impactMean,
                    data.impactMedian,
                    data.impactMode,
                  ])
                )
              case 'yesterdayImpact': {
                return [['姓名', 'Email', '昨日影响力']].concat(
                  dataSource.map((data: any) => [data.name, data.email, data.impact])
                )
              }
            }
          })()

          const csvContent = 'data:text/csv;charset=utf-8,\ufeff' + csv.map((row) => row.join(',')).join('\n')
          const encodedUri = encodeURI(csvContent)
          const link = document.createElement('a')
          link.setAttribute('href', encodedUri)
          link.setAttribute(
            'download',
            `Tale ${type} 记录 - ${
              type === 'yesterdayImpact' ? moment().format('YYYY-MM-DD') : yearMonth.format('YYYY-MM')
            }.csv`
          )
          document.body.appendChild(link)
          link.click()
          link.remove()
        }}
      >
        下载
      </Button>
      <Dropdown overlay={menu}>
        <a style={{ fontSize: 15 }}>
          类别：{type} <DownOutlined />
        </a>
      </Dropdown>
      <DatePicker
        disabled={type === 'yesterdayImpact'}
        picker='month'
        value={yearMonth}
        onChange={(value) => value && setYearMonth(value)}
      />
    </div>
  )

  return (
    <div className={s.legacy}>
      <div className={s.actions}>{actions}</div>
      <Table dataSource={dataSource as any} columns={columns} />
    </div>
  )
}
